// @flow

import {PermissionsAwareStorageManager} from '../PermissionsAwareStorageManager';
import {LocalStorageManifest} from './LocalStorageManifest';

import type {EnforcementMode} from '../types';

/**
 * Configuration options which can be passed to the constructor of LocalStorage.
 */
export type LocalStorageConfig = {|
  /**
   * By default, the library will make an XHR call to determine whether the user is currently
   * located in an "enforcement zone" where cookie permissions are required before setting cookies.
   * Passing a value for enforcementMode will skip this call and use the specified value instead.
   */
  enforcementMode?: EnforcementMode,
|};

/**
 * Allows reading and writing of local storage items while honoring a user's permissions.
 * @param {LocalStorageConfig} config Optional configuration options.
 */
export class LocalStorage extends PermissionsAwareStorageManager {
  constructor(config: LocalStorageConfig = {...null}) {
    super(config);
  }

  /**
   * Reads the value of the specified item from local storage.
   * @param {string} name The name of the item to read.
   * @returns The value of the item, or undefined if no value is set.
   */
  get(name: string): ?string {
    const item = LocalStorageManifest.get(name);
    return localStorage.getItem(name);
  }

  /**
   * Attempts to write the specified value to local storage. This will only succeed if:
   *
   *   1. The item belongs to a necessary category, OR
   *   2. The user has set permissions which allow items of the category
   *      to which the item belongs, OR
   *   3. The enforcement mode is currently set to "open", indicating that
   *      the current user is not located within an enforcement zone.
   *
   * @param {string} name The name of the item to write.
   * @param {string} value The value to write for the item.
   * @returns A promise for a value which indicates whether the item was successfully set.
   */
  async set(name: string, value: string): Promise<boolean> {
    const item = LocalStorageManifest.get(name);

    // If the item is in a category that isn't allowed for the user, block it.
    const isAllowed = await this.isCategoryAllowed(item.category);
    if (!isAllowed) return false;

    localStorage.setItem(name, value);

    return true;
  }

  /**
   * Removes the specified item from local storage.
   * @param {string} name The name of the item to delete.
   */
  remove(name: string): void {
    const item = LocalStorageManifest.get(name);
    localStorage.removeItem(name);
  }
}
